import { precoAvulsoDigitado } from '../precos/entradaPreco'
import { formatarMoeda } from '../lib/formatos'
import type { DicaDoPreco } from './precoNoCadastro'

// ▶ O que fazer com o preco da UNIDADE SOLTA digitado na ficha do produto.
//
// Por que isto existe: o preco avulso mora em `precos.preco_unidade` desde a
// migration 0021, e ja e editavel na tela da TABELA DE PRECO -- mas nao existia
// na FICHA. Foi exatamente a critica dela em 02/09, no celular: "nao tem campo
// para colocar unidade e valor da unidade".
//
// Por que e um modulo separado de `decidirPrecoDoCadastro`: as regras sao
// diferentes de proposito. No campo da caixa, vazio e "nao mexer" e zero e um
// preco de R$ 0,00 legitimo. Aqui, vazio e "nao mexer" e ZERO E UMA RESPOSTA --
// quer dizer "esta tabela nao vende unidade solta" (R12, decisao dela em
// 29/08). Enfiar as duas regras na mesma funcao faria uma delas vencer a outra
// com o tempo.
export type ResultadoAvulso =
  | { tipo: 'nao_mexer' }
  | { tipo: 'gravar'; avulso: number | null }
  | { tipo: 'erro'; mensagem: string }

export function decidirAvulsoDoCadastro(
  tabelaId: string,
  textoAvulso: string,
  // A mesma gravacao vai escrever o preco da caixa? (`decidirPrecoDoCadastro`
  // devolveu 'gravar')
  vaiGravarPreco: boolean,
  // Ja existe linha de `precos` deste produto nesta tabela?
  jaTemLinhaDePreco: boolean,
): ResultadoAvulso {
  if (textoAvulso.trim() === '') return { tipo: 'nao_mexer' }

  // A MESMA frase que `decidirPrecoDoCadastro` devolve, de proposito: duas
  // mensagens diferentes para o mesmo erro viram duas regras na cabeca de quem
  // usa.
  const avulso = precoAvulsoDigitado(textoAvulso)
  if (tabelaId === '') {
    // Zero sem tabela nao e preco nenhum: e o "0,00" com que o rascunho de
    // editar comeca. Sem isto, editar um produto sem tabela escolhida nao
    // salvava nem o NCM (achado do e2e da tela nova, 30/09).
    if (avulso === null) return { tipo: 'nao_mexer' }
    return { tipo: 'erro', mensagem: 'Escolha a tabela de preço antes de informar o valor.' }
  }

  // ⚠ `undefined` = nao consegui ler. Na tela da tabela isso significa "nao
  // mexe", porque la o campo grava na saida de foco e um dedo trocado nao pode
  // apagar em silencio. Aqui a ficha tem area de erro e botao Salvar, entao
  // DIZER e melhor do que engolir: a pessoa toca em Salvar esperando que o
  // numero entre.
  if (avulso === undefined) {
    return { tipo: 'erro', mensagem: 'Preço da unidade inválido. Use o formato 1.234,56.' }
  }

  // ⛔ Avulso sozinho NAO cria linha de preco -- a mesma regra que
  // EdicaoTabelaPreco ja aplica. Sem ela, "vende a unidade a 6,06" poria na
  // tabela um produto sem preco de caixa, que e exatamente o estado que a tela
  // chama de "sem preco nesta tabela".
  if (!vaiGravarPreco && !jaTemLinhaDePreco) {
    return {
      tipo: 'erro',
      mensagem:
        'Informe primeiro o preço da caixa: o preço da unidade sozinho não cria o preço nesta tabela.',
    }
  }

  return { tipo: 'gravar', avulso }
}

// ▶ O que o rotulo do campo avulso pode AFIRMAR, em cada estado. Irma de
// `dicaDoPreco`, com uma diferenca que importa: aqui o `exemplo` e SEMPRE vazio.
// No campo da caixa o "0,00" e um exemplo inofensivo; aqui o zero e a resposta
// "nao vende avulso", e oferece-lo como exemplo ensinaria a resposta errada.
export function dicaDoAvulso(tabelaId: string, avulsoAtual: number | null): DicaDoPreco {
  if (tabelaId === '') {
    return { sufixo: ' — escolha a tabela para ver o preço', campoLigado: false, exemplo: '' }
  }
  return {
    // ⚠ `!= null` pega null E undefined, e os dois importam: `null` e "esta
    // tabela nao vende avulso", e `undefined` e a linha de `precos` em cache de
    // antes da 0021, que nao tem a chave. Um `!== null` aqui escreveria
    // "R$ NaN" no aparelho de quem nao sincronizou.
    sufixo:
      avulsoAtual != null
        ? ` — atual: ${formatarMoeda(avulsoAtual)}`
        : ' — esta tabela não vende unidade avulsa',
    campoLigado: true,
    exemplo: '',
  }
}
